import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

interface ProtectedRouteProps {
    children: React.ReactNode
    roles?: ('worker' | 'employer')[]
    requireVerification?: boolean
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    roles,
    requireVerification = false
}) => {
    const location = useLocation()
    const { user, token, isLoading, isAuthenticated } = useSelector(
        (state: RootState) => state.auth
    )

    // Show loading spinner while checking authentication
    // Show loading if we're still loading OR if we have a token but no user yet (initial auth check)
    if (isLoading || (token && !user)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Verifying access..." />
            </div>
        )
    }

    // Redirect to login if not authenticated
    if (!token || !isAuthenticated || !user) {
        // Debug log for development
        if (process.env.NODE_ENV === 'development') {
            console.log('ProtectedRoute: Redirecting to login', {
                token: !!token,
                isAuthenticated,
                user: !!user,
                location: location.pathname
            })
        }

        return (
            <Navigate
                to="/login"
                state={{ from: location }}
                replace
            />
        )
    }

    // Check role-based access
    if (roles && !roles.includes(user.role)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mx-4 text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full mx-auto mb-4">
                        <svg
                            className="w-6 h-6 text-yellow-600 dark:text-yellow-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Access Restricted
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        You don't have permission to access this page. This area is restricted to {roles?.join(' and ')} accounts.
                    </p>
                    <button
                        onClick={() => window.history.back()}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-200"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    // Check account status restrictions - banned users cannot access protected routes
    if (user.isBanned) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mx-4 text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full mx-auto mb-4">
                        <svg
                            className="w-6 h-6 text-red-600 dark:text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
                        Account Banned
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        {user.bannedReason ? `Your account has been permanently banned. Reason: ${user.bannedReason}` : 'Your account has been permanently banned.'}
                    </p>
                    <p className="text-gray-500 dark:text-gray-500 text-xs mb-4">
                        Contact support if you believe this is an error.
                    </p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors duration-200"
                    >
                        Return to Home
                    </button>
                </div>
            </div>
        )
    }

    // Check email verification requirement
    if (requireVerification && !user.emailVerified) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mx-4 text-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full mx-auto mb-4">
                        <svg
                            className="w-6 h-6 text-blue-600 dark:text-blue-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Email Verification Required
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        Please verify your email address to access this feature. Check your inbox for a verification link.
                    </p>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={async () => {
                                try {
                                    // Import resendVerification action dynamically to avoid circular import
                                    const { resendVerification } = await import('@/store/slices/authSlice')
                                    const { store } = await import('@/store/store')
                                    await store.dispatch(resendVerification())
                                } catch (error) {
                                    console.error('Failed to resend verification:', error)
                                }
                            }}
                            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-200"
                        >
                            Resend Verification
                        </button>
                        <button
                            onClick={() => window.history.back()}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors duration-200"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return <>{children}</>
}

export default ProtectedRoute